import React from "react";

import { useMessages } from "../../context/MessagesContext";
import { useRadar } from "../../context/RadarContext";
import Fadeable from "../Fadeable/Fadeable";
import HeadlineGroup from "../HeadlineGroup/HeadlineGroup";
import HeroHeadline from "../HeroHeadline/HeroHeadline";
import SetTitle from "../SetTitle";
import "./PageHelp.scss";

interface Props {
  leaving: boolean;
  onLeave: () => void;
}

const PageHelp: React.FC<Props> = ({ leaving, onLeave }) => {
  const { pageHelp } = useMessages();
  const { descriptor } = useRadar();

  if (pageHelp) {
    const {
      headlinePrefix,
      headline,
      intro,
      paragraphs = [],
      quadrantsPreDescription,
      quadrants = [],
      ringsPreDescription,
      rings = [],
      sourcecodeLink,
    } = pageHelp;
    const title = `${headlinePrefix || "How to use the"} ${descriptor.name}`;
    return (
      <Fadeable leaving={leaving} onLeave={onLeave}>
        <SetTitle title={title} />
        <HeroHeadline>{title}</HeroHeadline>
        <div className="root">
          <div className="area">
            <div className="area-text">
              <div className="title">
                {headline || `Unleash innovation with the ${descriptor.name}`}
              </div>
              <div className="main-text">{intro}</div>
            </div>
            <div className="rectangle-area">
              <div className="rectangle-pink"></div>
              <div className="rectangle-blue">
                <div className="square-blue"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-help">
          {paragraphs.map((paragraph) => (
            <section className="page-help__section" key={paragraph.headline}>
              <HeadlineGroup secondary>
                <h4 className="headline headline--dark">
                  {paragraph.headline}
                </h4>
              </HeadlineGroup>
              {paragraph.values.map((value, index) => (
                <p className="page-help__text" key={index}>
                  {value}
                </p>
              ))}
            </section>
          ))}

          {Boolean(quadrants.length) && (
            <section className="page-help__section">
              <HeadlineGroup secondary>
                <h4 className="headline headline--dark">Quadrants</h4>
              </HeadlineGroup>
              {quadrantsPreDescription && (
                <p className="page-help__text">{quadrantsPreDescription}</p>
              )}
              <dl className="page-help__definitions">
                {quadrants.map((quadrant) => (
                  <React.Fragment key={quadrant.name}>
                    <dt>{quadrant.name}</dt>
                    <dd>{quadrant.description}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          )}

          {Boolean(rings.length) && (
            <section className="page-help__section">
              <HeadlineGroup secondary>
                <h4 className="headline headline--dark">Rings</h4>
              </HeadlineGroup>
              {ringsPreDescription && (
                <p className="page-help__text">{ringsPreDescription}</p>
              )}
              <dl className="page-help__definitions">
                {rings.map((ring) => (
                  <React.Fragment key={ring.name}>
                    <dt>{ring.name}</dt>
                    <dd>{ring.description}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          )}

          {sourcecodeLink && (
            <section className="page-help__section">
              <p className="page-help__text">
                {sourcecodeLink.description}{" "}
                <a
                  href={sourcecodeLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {sourcecodeLink.name}
                </a>
              </p>
            </section>
          )}
        </div>
      </Fadeable>
    );
  }

  return null;
};

export default PageHelp;
